import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { StackChart } from "@/charts/StackChart";
import { ActionCategory } from "@/generated/core/v1/actions";
import { ChartBox } from "@/components/ChartInfo";
import { type HeadCard, Headline } from "@/components/Headline";
import { money } from "@/plan-logic/lib/format";
import {
  educationRows,
  firstState,
  flatActions,
  hasResp,
  homePurchase,
  impactShares,
  priorityOutcomes,
  type PriorityOutcome,
  RETIREMENT_ACCOUNT_KINDS,
  sumKinds,
  totalGrants,
} from "@/plan-logic/plan-view";
import { RetirementMethod } from "@/generated/core/v1/family";
import { PriorityList, PriorityPicker, type RetirementTarget } from "@/priorities/PriorityEditor";
import { emptyQuestionnaire } from "@/plan-logic/questionnaire/build";
import { priorityFieldErrors } from "@/questionnaire/priorityErrors";
import type { DraftPriority } from "@/plan-logic/questionnaire/draft";
import {
  kidsFromFamily,
  prioritiesFromFamily,
  prioritiesSection,
  retirementFromFamily,
} from "@/plan-logic/questionnaire/fromFamily";
import {
  DEFAULT_RETIREMENT_AGE,
  householdRetirementCheck,
  retirementAgeOptions,
  type SavingAdult,
} from "@/questionnaire/retirementAge";
import { validateDraft } from "@/onboarding/schema";
import { emptyDraft } from "@/plan-logic/questionnaire/draft";
import { PlanPage, type PlanCtx } from "../PlanPage";
import { SaveFeedback, useFamilySave } from "../useFamilySave";
import { homeLine, outcomeLabel, winLabel } from "@/plan-logic/plan-view/outcomeText";

function note(o: PriorityOutcome): string {
  const a = Math.min(1, o.abi?.completion ?? 0);
  const n = Math.min(1, o.naive?.completion ?? 0);
  if (o.isRetirement) {
    if (a >= 0.995) return "Your plan reaches the nest egg your retirement needs.";
    return `Your plan reaches ${Math.round(a * 100)}% of the nest egg your retirement needs.`;
  }
  const purchase = o.home ? homePurchase(o.abi) : undefined;
  if (purchase === "bought")
    return `${n <= 0.005 ? "Not reachable on the path you're on today. " : ""}Abi’s plan buys it on the date, with a mortgage for the rest.`;
  if (purchase === "skipped")
    return "Not bought on this plan: on that date your savings outside your RRSP don’t cover the whole down payment. A later date or a higher score may change that.";
  if (a >= 0.995 && n <= 0.005)
    return "Not reachable on the path you're on today. The plan frees up the money to fund it.";
  if (a >= 0.995) return "Fully funded on Abi’s plan.";
  if (a <= 0.005) return "Out of reach for now. Raising its score, or its date, changes that.";
  return `About ${Math.round(a * 100)}% funded on Abi’s plan. Raising its score sends more toward it.`;
}

const pc = (f: number) => `${Math.round(Math.max(0, Math.min(1, f)) * 100)}%`;

/**
 * The priorities headline (v45 `headPri`): the top priority funded today, how many are fully
 * funded on the current path, and on Abi's plan. "Funded today" is only measurable for retirement
 * (the retirement accounts against the engine's nest egg); for any other top priority it's the
 * share the current path funds by the date set, said as such.
 */
function priorityHeadCards(ctx: PlanCtx, outcomes: PriorityOutcome[]): HeadCard[] {
  const top = outcomes[0];
  const n = outcomes.length;
  const full = (which: "abi" | "naive") =>
    outcomes.filter((o) => Math.round(Math.min(1, o[which]?.completion ?? 0) * 100) >= 100).length;
  const cards: HeadCard[] = [];
  if (top?.isRetirement && top.abi) {
    const held = sumKinds(firstState(ctx.abi), RETIREMENT_ACCOUNT_KINDS);
    const need = top.abi.required;
    cards.push({
      k: "Your top priority, today",
      v: `${pc(need > 0 ? held / need : 0)} funded`,
      s: `${top.name} · ${money(held)} in your retirement accounts against the ${money(need)} it takes`,
    });
  } else if (top) {
    const f = top.naive ?? top.abi;
    cards.push({
      k: "Your top priority",
      v: `${pc(f?.completion ?? 0)} funded`,
      s: `${top.name} · ${top.naive ? "on your current path" : "on Abi’s plan"}, by the date you set`,
    });
  }
  if (ctx.naive) {
    const nowN = full("naive");
    const abiN = full("abi");
    const d = abiN - nowN;
    const goals = (k: number) => `${k} ${Math.abs(k) === 1 ? "goal" : "goals"}`;
    cards.push(
      {
        k: "On your current plan",
        v: `${nowN} of ${n} fully funded`,
        s: "by the dates you set, if nothing changes",
      },
      {
        k: "With Abi’s plan",
        abi: true,
        from: String(nowN),
        to: `${abiN} of ${n} fully funded`,
        lead: d > 0 ? `+${goals(d)}` : d < 0 ? `−${goals(-d)}` : "No change",
        leadTone: d > 0 ? "ok" : d < 0 ? "debt" : "flat",
        s:
          d > 0
            ? `${d === 1 ? "reaches" : "reach"} 100% by the date you chose`
            : d < 0
              ? "fewer reach 100% by the dates you chose"
              : "in how many reach 100% by the dates you chose",
        testId: "pri-ontrack",
      },
    );
  } else {
    cards.push({
      k: "With Abi’s plan",
      abi: true,
      v: `${full("abi")} of ${n} fully funded`,
      s: "by the dates you set",
      testId: "pri-ontrack",
    });
  }
  return cards;
}

function StandView({ ctx }: { ctx: PlanCtx }) {
  const { family, abi, naive, index } = ctx;
  const outcomes = useMemo(() => priorityOutcomes(family, abi, naive), [family, abi, naive]);
  const shares = useMemo(
    () => new Map(impactShares(outcomes).map((s) => [s.outcome.key, s.share])),
    [outcomes],
  );
  const edu = hasResp(abi) ? educationRows(abi) : [];
  const respKids = family.dependents;
  const contributing = flatActions(abi).some(
    (f) => f.category === ActionCategory.SAVING && index.get(f.targetUuid)?.kindLabel === "RESP",
  );

  return (
    <div id="pr-view-0">
      <Headline id="hd-pri" cards={priorityHeadCards(ctx, outcomes)} />
      <div className="card pad">
        <div id="prios" data-testid="pri-list">
          {outcomes.map((o, i) => {
            const share = shares.get(o.key);
            const bad = (o.abi?.completion ?? 0) < (o.naive?.completion ?? 0) - 0.005;
            return (
              <div
                key={o.key}
                style={{
                  padding: `${i ? 20 : 0}px 0 20px`,
                  borderBottom: i < outcomes.length - 1 ? "1px solid var(--line2)" : 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 10,
                    marginBottom: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <b style={{ fontSize: 16 }}>{o.name}</b>
                  <span className="lvlchip">{o.importance} out of 10</span>
                  <span
                    className="pill"
                    style={
                      bad
                        ? { background: "var(--warnbg)", color: "var(--warn)", marginLeft: "auto" }
                        : { marginLeft: "auto" }
                    }
                  >
                    {winLabel(o)}
                  </span>
                </div>
                {o.home && (
                  <p className="mut" style={{ margin: "-4px 0 10px" }} data-testid="pri-home">
                    {homeLine(o.home)}
                  </p>
                )}
                {naive && (
                  <div className="bar-t">
                    <div
                      className="bar-f"
                      style={{
                        width: `${Math.round(Math.min(1, o.naive?.completion ?? 0) * 100)}%`,
                        background: "var(--before)",
                      }}
                    />
                  </div>
                )}
                <div className="bar-t">
                  <div
                    className="bar-f"
                    style={{
                      width: `${Math.round(Math.min(1, o.abi?.completion ?? 0) * 100)}%`,
                      background: "var(--after)",
                    }}
                  />
                </div>
                <div className="legend" style={{ marginTop: 8 }}>
                  {naive && (
                    <span>
                      <i className="lgd" style={{ background: "var(--before)" }} />
                      {outcomeLabel(o, "naive")}
                    </span>
                  )}
                  <span>
                    <i className="lgd" style={{ background: "var(--after)" }} />
                    {outcomeLabel(o, "abi")}
                  </span>
                  {share ? (
                    <span style={{ marginLeft: "auto", color: "var(--ink3)" }}>
                      {Math.round(share * 100)}% of the whole improvement
                    </span>
                  ) : null}
                </div>
                <p style={{ marginTop: 9, fontSize: 13.5, color: "var(--ink2)" }}>{note(o)}</p>
              </div>
            );
          })}
        </div>
        <p className="hint">
          The share on the right is how much of the plan&rsquo;s total improvement each priority
          accounts for: how much more of the goal gets paid for, weighted by the score you gave it.
        </p>
      </div>

      {edu.length > 0 && (
        <div className="card pad" style={{ marginTop: 16 }}>
          <h2 style={{ marginBottom: 4 }}>
            {respKids.length === 1
              ? `${respKids[0].givenName || "Your child"}’s education fund`
              : "Your children’s education fund"}
            , building up and drawing down
          </h2>
          <p className="mut" style={{ marginBottom: 16, maxWidth: "66ch" }}>
            {contributing ? "Contributions while they're young, then" : "What's in the RESP, then"}{" "}
            school paying it back out. The grant is the part you only get by putting money in before
            each child turns 17.
          </p>
          <ChartBox id="edu-chart">
            <StackChart
              rows={edu.map((r, i) => ({
                label: i ? r.year : "Now",
                values: { con: r.contributed, grant: r.grant, grow: r.growth },
              }))}
              series={[
                { k: "con", n: "What you put in", c: "var(--after)" },
                { k: "grant", n: "Government grant", c: "var(--save)" },
                { k: "grow", n: "Growth", c: "var(--life)" },
              ]}
              barWidth={40}
              height={280}
              label="Education fund building up then drawing down"
              note={`Government grants in the plan come to $${Math.round(totalGrants(abi)).toLocaleString("en-CA")} in total.`}
              testId="edu-chart"
              colLabel="In the fund"
            />
          </ChartBox>
        </div>
      )}
    </div>
  );
}

/** "Retiring comfortably" carries the share of today's income and the age (v39), as in Update Data. */
function storedRetirement(family: PlanCtx["family"]): RetirementTarget {
  const r = retirementFromFamily(family);
  return {
    pct: r.method === RetirementMethod.TARGET ? r.pct : undefined,
    age: r.age ?? DEFAULT_RETIREMENT_AGE,
  };
}

function EditView({ ctx }: { ctx: PlanCtx }) {
  const { family, doc, today } = ctx;
  const navigate = useNavigate();
  const save = useFamilySave(doc);
  const [list, setList] = useState<DraftPriority[]>(() => prioritiesFromFamily(family));
  const [retire, setRetire] = useState<number | undefined>(
    family.retirementPriorityLevel || undefined,
  );
  const [ret, setRet] = useState<RetirementTarget>(() => storedRetirement(family));
  const [retDirty, setRetDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  // The engine's field errors are about the values last sent: an edit hides them until the next save.
  const [engineCurrent, setEngineCurrent] = useState(false);

  // A newer family (a 409, or a save elsewhere) resets the form to it.
  useEffect(() => {
    setList(prioritiesFromFamily(family));
    setRetire(family.retirementPriorityLevel || undefined);
    setRet(storedRetirement(family));
    setRetDirty(false);
    setDirty(false);
    setEngineCurrent(false);
  }, [family]);

  // The engine's errors on a priority's field (the home-buying minimum down payment, ...) show beside it.
  const engine = useMemo(
    () => priorityFieldErrors(save.fieldErrors, list),
    [save.fieldErrors, list],
  );
  const kids = kidsFromFamily(family);
  const hasDebt =
    family.loans.length > 0 ||
    family.mortgages.length > 0 ||
    family.persons.some((p) => p.creditCards.length > 0);

  const adults: SavingAdult[] = family.persons.map((p, i) => ({
    name: i ? p.givenName.trim() || "Your partner" : "",
    dob: p.dob,
    storedAge: p.retirementGoal?.retirementAge,
  }));
  const fromSpending = retirementFromFamily(family).method !== RetirementMethod.TARGET;

  const submit = async () => {
    const issues = validateDraft({
      ...emptyDraft(),
      priorities: list,
      retirementImportance: retire,
    }).filter((i) => i.path.startsWith("priorities") || i.path === "retirementImportance");
    // the age is checked as Update Data checks it: still ahead of every adult
    const ageIssue = retDirty
      ? householdRetirementCheck(
          adults,
          { target: true, age: ret.age, storedAge: retirementFromFamily(family).age },
          today,
        ).issue
      : undefined;
    if (ageIssue) issues.push({ path: "retirement.age", message: ageIssue });
    setErrors(Object.fromEntries(issues.map((i) => [i.path, i.message])));
    if (issues.length) return;
    const q = emptyQuestionnaire(today);
    q.priorities = prioritiesSection(list, retire, family);
    if (retDirty)
      q.retirement =
        ret.pct !== undefined
          ? {
              method: RetirementMethod.TARGET,
              incomeReplacementPercent: ret.pct,
              retirementAge: ret.age,
            }
          : { method: RetirementMethod.SPENDING, retirementAge: ret.age };
    setEngineCurrent(true);
    if (await save.run(q)) {
      setDirty(false);
      navigate("/app/priorities");
    }
  };

  return (
    <div id="pr-view-1">
      <div className="card pad" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 4 }}>Add a priority</h2>
        <p className="mut" style={{ marginBottom: 14 }}>
          Tap to add. Save when you&rsquo;re done, and Abi rebuilds your plan around the new list.
        </p>
        <PriorityPicker
          priorities={list}
          onAdd={(p) => {
            setList([...list, p]);
            setDirty(true);
            setEngineCurrent(false);
          }}
        />
      </div>
      <div className="card pad">
        <h2 style={{ marginBottom: 4 }}>Your priorities</h2>
        <p className="mut" style={{ marginBottom: 14 }}>
          Score each one from 1 to 10. Ties are fine &mdash; if three things matter equally, give
          all three a 10.
        </p>
        <div className="act-body" id="pri-list">
          <PriorityList
            priorities={list}
            onChange={(next) => {
              setList(next);
              setDirty(true);
              setEngineCurrent(false);
            }}
            retirementImportance={retire}
            onRetirementImportance={(v) => {
              setRetire(v);
              setDirty(true);
              setEngineCurrent(false);
            }}
            retirement={ret}
            onRetirement={(next) => {
              setRet(next);
              setRetDirty(true);
              setDirty(true);
              setEngineCurrent(false);
            }}
            ageOptions={retirementAgeOptions(adults, today)}
            fromSpending={fromSpending}
            hasDebt={hasDebt}
            kids={kids}
            err={(p) => errors[p] ?? (engineCurrent ? engine.byField[p] : undefined)}
          />
        </div>
      </div>
      <SaveFeedback
        state={{
          ...save,
          fieldErrors: engine.rest,
          // every engine error shown beside its field: no list, and no "not valid" line either
          error: save.fieldErrors.length && !engine.rest.length ? undefined : save.error,
        }}
      />
      <div className={`edsave${dirty ? " on" : ""}`}>
        <div>
          <b>Changes aren&rsquo;t live until you recalculate.</b>
          <span>
            Abi reruns the whole plan against your new priorities. It usually takes under a minute,
            and your actions and dates may move.
          </span>
        </div>
        <button
          type="button"
          className="btn"
          onClick={submit}
          disabled={save.pending}
          data-testid="pri-save"
        >
          {save.pending ? "Saving…" : "Save and recalculate"}
        </button>
      </div>
    </div>
  );
}

export function PrioritiesPage({ edit = false }: { edit?: boolean }) {
  const navigate = useNavigate();
  return (
    <PlanPage
      title="pages.priorities.title"
      sub="pages.priorities.sub"
      testId={edit ? "priorities-edit" : "priorities"}
      headRight={
        <div className="cfvtabs" style={{ margin: 0 }}>
          <button
            type="button"
            className={`prtab${!edit ? " on" : ""}`}
            onClick={() => navigate("/app/priorities")}
          >
            Where they stand
          </button>
          <button
            type="button"
            className={`prtab${edit ? " on" : ""}`}
            onClick={() => navigate("/app/priorities/edit")}
            data-testid="pri-edit-tab"
          >
            Edit priorities
          </button>
        </div>
      }
    >
      {(ctx) => (edit ? <EditView ctx={ctx} /> : <StandView ctx={ctx} />)}
    </PlanPage>
  );
}
